import React from "react";

type RateLimiterOptions = {
  MaxConcurrent: number;
  MinTimeBetweenStartsMs: number;
  MaxRetriesOn429: number;
  DefaultRetryDelayMs: number;
};

declare global {
  interface Window {
    __FetchRateLimiterInstalled?: boolean;
    __FetchRateLimiterOrigFetch?: typeof fetch;
  }
}

type Task<T> = {
  Run: () => Promise<T>;
  Resolve: (value: T) => void;
  Reject: (reason?: unknown) => void;
};

function Sleep(Ms: number): Promise<void> {
  return new Promise((Resolve) => window.setTimeout(Resolve, Ms));
}

function ParseRetryAfterMs(HeaderValue: string | null): number | null {
  if (!HeaderValue) return null;
  const AsSeconds = Number(HeaderValue);
  if (Number.isFinite(AsSeconds) && AsSeconds >= 0) return Math.round(AsSeconds * 1000);
  const AsDate = Date.parse(HeaderValue);
  if (!Number.isFinite(AsDate)) return null;
  const Delta = AsDate - Date.now();
  return Delta > 0 ? Delta : 0;
}

class PromiseQueue {
  private Active = 0;
  private LastStartAt = 0;
  private Queue: Array<Task<any>> = [];
  private Options: RateLimiterOptions;
  private TimerId: number | null = null;

  constructor(Options: RateLimiterOptions) {
    this.Options = Options;
  }

  Enqueue<T>(Run: () => Promise<T>): Promise<T> {
    return new Promise<T>((Resolve, Reject) => {
      this.Queue.push({ Run, Resolve, Reject });
      this.Pump();
    });
  }

  private Pump() {
    // If a start is already scheduled, don't schedule another.
    if (this.TimerId !== null) return;
    if (this.Active >= this.Options.MaxConcurrent) return;
    if (this.Queue.length === 0) return;

    const Now = Date.now();
    const SinceLast = Now - this.LastStartAt;
    const WaitMs = Math.max(0, this.Options.MinTimeBetweenStartsMs - SinceLast);

    this.TimerId = window.setTimeout(() => {
      this.TimerId = null;

      if (this.Active >= this.Options.MaxConcurrent) {
        this.Pump();
        return;
      }

      const Next = this.Queue.shift() as Task<any> | undefined;
      if (!Next) return;

      this.LastStartAt = Date.now();
      this.Active += 1;

      (Next.Run() as Promise<unknown>)
        .then(
          (Result) => Next.Resolve(Result),
          (Err) => Next.Reject(Err),
        )
        .finally(() => {
          this.Active -= 1;
          this.Pump();
        });

      // Schedule next start (cadenced) if more work exists.
      this.Pump();
    }, WaitMs);
  }
}

function InstallFetchRateLimiter(Overrides?: Partial<RateLimiterOptions>) {
  if (!window.__FetchRateLimiterOrigFetch) {
    window.__FetchRateLimiterOrigFetch = window.fetch.bind(window);
  }
  const NativeFetch: typeof fetch = window.__FetchRateLimiterOrigFetch;

  if (window.__FetchRateLimiterInstalled) {
    return () => {};
  }

  const Options: RateLimiterOptions = {
    // Backend is 100 req/s with burst 300; frontend can be more aggressive with higher burst capacity.
    MaxConcurrent: 10,
    MinTimeBetweenStartsMs: 10, // 100 req/s = 10ms between requests
    MaxRetriesOn429: 2,
    DefaultRetryDelayMs: 750,
    ...(Overrides || {}),
  };

  const Queue = new PromiseQueue(Options);

  const LimitedFetch: typeof fetch = (async (Input: RequestInfo | URL, Init?: RequestInit) => {
    return Queue.Enqueue(async () => {
      let Attempt = 0;

      while (true) {
        const Response = await NativeFetch(Input, Init);
        if (Response.status !== 429) return Response;

        if (Attempt >= Options.MaxRetriesOn429) return Response;
        Attempt += 1;

        const RetryAfterMs = ParseRetryAfterMs(Response.headers.get("Retry-After"));
        const BackoffMs = Options.DefaultRetryDelayMs * Math.pow(2, Attempt - 1);
        const WaitMs = RetryAfterMs ?? BackoffMs;
        await Sleep(WaitMs);
      }
    });
  }) as typeof fetch;

  window.fetch = LimitedFetch;
  window.__FetchRateLimiterInstalled = true;

  return () => {
    if (window.__FetchRateLimiterOrigFetch) {
      window.fetch = window.__FetchRateLimiterOrigFetch;
    }
    window.__FetchRateLimiterInstalled = false;
  };
}

type FetchRateLimiterContextValue = {
  isInstalled: boolean;
};

const FetchRateLimiterContext = React.createContext<FetchRateLimiterContextValue | null>(null);

export function FetchRateLimiterProvider({ children }: { children: React.ReactNode }) {
  const [isInstalled, setIsInstalled] = React.useState(false);

  React.useEffect(() => {
    const uninstall = InstallFetchRateLimiter();
    setIsInstalled(true);
    return () => {
      uninstall();
      setIsInstalled(false);
    };
  }, []);

  const value = React.useMemo(() => ({ isInstalled }), [isInstalled]);

  return (
    <FetchRateLimiterContext.Provider value={value}>{children}</FetchRateLimiterContext.Provider>
  );
}

export function useFetchRateLimiter() {
  const ctx = React.useContext(FetchRateLimiterContext);
  if (!ctx) throw new Error("useFetchRateLimiter must be used within FetchRateLimiterProvider");
  return ctx;
}
